import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import type { PosBank, PosGetItemByBarcodeResponse } from '@/api/types'

function unwrap(payload: unknown): unknown {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if ('success' in p && 'data' in p) return p.data
    if ('data' in p) return p.data
  }
  return payload
}

export function isoDateOnly(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export async function getPosItemByBarcode(params: {
  branchId: number
  barcode: string
  orderDate?: string
}): Promise<PosGetItemByBarcodeResponse | null> {
  const barcode = params.barcode.trim()
  if (!barcode) return null

  const url = endpoints.pos.getItemByBarcode({
    branchId: params.branchId,
    barcode,
    orderDate: (params.orderDate || '').trim() || isoDateOnly(new Date()),
  })

  const res = await apiClient.get<ApiResponse<unknown> | unknown>(url, {
    // allow caller to handle "not found" / validation UI without global SweetAlerts
    skipHandledLocallyErrorUI: true,
    skipGlobal500Swal: true,
  } as any)

  const raw = unwrap(res.data)
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  return raw as PosGetItemByBarcodeResponse
}

function toArray(payload: unknown): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: any[] }).data
  }
  if (raw && typeof raw === 'object') return [raw]
  return []
}

function mapBank(x: Record<string, unknown>): PosBank | null {
  const id = Number(x.bankId ?? x.BankId ?? x.id ?? x.Id ?? 0)
  if (!Number.isFinite(id) || id <= 0) return null
  return {
    bankId: id,
    bankNameEn: String(x.bankNameEn ?? x.BankNameEn ?? x.nameEn ?? x.NameEn ?? x.bankName ?? x.BankName ?? ''),
    bankNameAr: String(x.bankNameAr ?? x.BankNameAr ?? x.nameAr ?? x.NameAr ?? ''),
  }
}

export async function getPosBanks(branchId: number): Promise<PosBank[]> {
  const res = await apiClient.get<ApiResponse<unknown> | unknown>(endpoints.pos.getBanks(branchId), {
    skipHandledLocallyErrorUI: true,
    skipGlobal500Swal: true,
  } as any)
  const arr = toArray(res.data)
  const out: PosBank[] = []
  for (const row of arr) {
    if (row && typeof row === 'object') {
      const b = mapBank(row as Record<string, unknown>)
      if (b) out.push(b)
    }
  }
  return out
}

export type PosSaveInvoiceLine = {
  itemId: number
  batchId: number
  quantity: number
  unitCostPrice: number
  unitSellingPrice: number
  discount: number
  discountPercent: number
  itemPrice: number
  taxableAmount: number
  taxId: number
  totalTax: number
  taxPercent: number
  bonusId: number
  bonusItemId: number
  bonusBatchId: number
  bonusQuantity: number
  lineAmount: number
}

export type PosSavePayload = {
  branchId: number
  subTotal: number
  totalTax: number
  totalDiscount: number
  total: number
  bankId: number
  invoiceLines: PosSaveInvoiceLine[]
}

export async function savePos(payload: PosSavePayload): Promise<unknown> {
  const res = await apiClient.post<ApiResponse<unknown> | unknown>(endpoints.pos.save, payload, {
    skipHandledLocallyErrorUI: true,
    skipGlobal500Swal: true,
  } as any)
  return res.data
}

export const posService = {
  getItemByBarcode: getPosItemByBarcode,
  getBanks: getPosBanks,
  save: savePos,
}

